import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Card, Loading, Button } from '../components/common';
import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const LiquidityPage = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [period, setPeriod] = useState('daily');

    useEffect(() => {
        fetchForecast();
    }, [period]);

    const fetchForecast = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/forecast?period=${period}`);
            if (response.data.success) {
                setData(response.data.data);
            }
        } catch (err) {
            console.error('Failed to fetch liquidity forecast:', err);
            setError(t('errors.generic') || 'Could not load data.');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'low_liquidity': return 'text-red-600 bg-red-50';
            case 'warning': return 'text-yellow-600 bg-yellow-50';
            case 'safe': return 'text-green-600 bg-green-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            low_liquidity: 'bg-red-100 text-red-800',
            warning: 'bg-yellow-100 text-yellow-800',
            safe: 'bg-green-100 text-green-800',
            default: 'bg-gray-100 text-gray-800'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.default}`}>
                {t(`liquidity.${status}`) || status}
            </span>
        );
    };

    if (loading) return <div className="p-8"><Loading size="lg" /></div>;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t('liquidity.title')}
                    </h1>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {t('liquidity.subtitle')}
                    </p>
                </div>
                <Button onClick={fetchForecast} variant="secondary">
                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                    {t('common.refresh') || 'Refresh'}
                </Button>
            </div>

            {error && (
                <div className="bg-red-50 text-red-700 p-4 rounded-lg border border-red-200">
                    {error}
                </div>
            )}

            {/* Main Content */}
            <Card>
                <div className="flex justify-between items-center mb-4 px-4 pt-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t('liquidity.title')}
                    </h2>
                    <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                        {['daily', 'weekly', 'monthly'].map((p) => (
                            <button
                                key={p}
                                onClick={() => setPeriod(p)}
                                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${period === p
                                        ? 'bg-white dark:bg-gray-600 text-primary-600 shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}
                            >
                                {t(`reports.${p === 'daily' ? 'dailyReport' : p === 'weekly' ? 'weeklyReport' : 'monthlyReport'}`).split(' ')[0] || p.charAt(0).toUpperCase() + p.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                                    {t('liquidity.currency')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                                    {t('reports.opening') || 'Opening'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                                    {t('reports.totalIn') || 'Total In'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                                    {t('reports.totalOut') || 'Total Out'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                                    {t('liquidity.currentBalance')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                                    {t('reports.totalProfit') || 'Profit'}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                                    {t('liquidity.status')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider rtl:text-right">
                                    {t('liquidity.recommendation')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {data && data.map((item) => (
                                <tr key={item.currencyCode} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-xs mr-3 rtl:mr-0 rtl:ml-3">
                                                {item.currencyCode.substring(0, 2)}
                                            </div>
                                            <span className="font-medium text-gray-900 dark:text-white">{item.currencyCode}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                        {item.openingBalance?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400">
                                        {item.volumeIn?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                                        {item.volumeOut?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300 font-medium">
                                        {item.currentBalance?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-bold">
                                        {item.totalProfit ? (
                                            item.totalProfit.toLocaleString(undefined, {
                                                minimumFractionDigits: 0,
                                                maximumFractionDigits: 0
                                            }) + ' ' + (item.currencySymbol || 'USD')
                                        ) : '0'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getStatusBadge(item.status)}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                        {item.status !== 'safe' ? (
                                            <span className="text-red-600 font-medium flex items-center">
                                                <ExclamationTriangleIcon className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1" />
                                                {item.recommendation}
                                            </span>
                                        ) : (
                                            <span className="text-green-600 flex items-center">
                                                <CheckCircleIcon className="h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1" />
                                                {t('liquidity.sufficientReserves')}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Instructions / Legend */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-blue-800 font-semibold mb-2 text-sm">{t('common.info') || 'Information'}</h3>
                    <p className="text-blue-700 text-sm">
                        {t('liquidity.infoText')}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LiquidityPage;
